// Copyright ©️ 2026 eVolve MEP, LLC

import { getBasicStoreListeners } from 'helpers/getBasicStoreListeners';

type ViewerMarkupSnapshot = {
  hasUnsavedChanges: boolean;
  /**
   * An action to prompt the user on. If the user confirms they want to perform the prompted action, call the function,
   * then set to `null` to mark that the action is no longer pending.
   * Otherwise, immediately set to `null` to dismiss the prompt.
   */
  promptAction: (() => void) | null;
};

/**
 * A per-viewer store for markup editor state, kept in sync by `MarkupModeExtension`.
 * Intended for use with React's `useSyncExternalStore`.
 *
 * One instance is created per `ViewerMarkupStoreProvider`, so multiple simultaneous
 * viewers never share or overwrite each other's state.
 */
export const createViewerMarkupStore = () => {
  let snapshot: ViewerMarkupSnapshot = {
    hasUnsavedChanges: false,
    promptAction: null,
  };

  const { subscribe, notifyListeners } = getBasicStoreListeners();

  const update = (partial: Partial<ViewerMarkupSnapshot>) => {
    snapshot = { ...snapshot, ...partial };
    notifyListeners();
  };

  return {
    getSnapshot: () => snapshot,
    subscribe,

    /** Called by `MarkupModeExtension` when the unsaved-changes state changes. */
    setHasUnsavedChanges: (hasUnsavedChanges: boolean) => update({ hasUnsavedChanges }),

    /**
     * Called by `MarkupModeExtension` when the editor's exit button is clicked with unsaved changes,
     * or by React consumers that need to guard an action behind a confirmation.
     * Set to `null` to dismiss the confirmation (either after confirming or cancelling).
     */
    setPromptAction: (promptAction: ViewerMarkupSnapshot['promptAction']) => update({ promptAction }),
    clear: () => update({ hasUnsavedChanges: false, promptAction: null }),
  };
};

export type ViewerMarkupStoreContextType = ReturnType<typeof createViewerMarkupStore>;
